import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';

import { SnCurrentOrganization } from '@lib/auth';
import { SnCameraFieldsGeneratorService } from '@lib/cameras/services/camera-fields-generator.service';
import { SnFloorFieldsGeneratorService } from '@lib/locations';
import { SnCamera } from 'libs/api/entities/src/lib/camera';
import { SnCamerasAPI } from 'libs/api/services/src/lib/cameras.service';
import { SnLocationsAPI } from 'libs/api/services/src/lib/locations.service';
import { ISnListQueryParameters } from 'libs/api/src/lib/interfaces/list-query-parameters.interface';
import { ISnHttpSelectFormFieldConfig } from 'libs/form/http-select/src/lib/interfaces/http-select-form-field-config.interface';
import { SnFormFieldConfig } from 'libs/form/src/lib/types/form-field-config.type';

import { FususCameraDetectionsDialogComponent } from '../fusus-camera-detections-dialog/fusus-camera-detections-dialog.component';

@Component({
  selector: 'sn-fusus-camera-form-dialog',
  templateUrl: './fusus-camera-form-dialog.component.html',
  styleUrls: ['./fusus-camera-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FususCameraFormDialogComponent implements OnDestroy, OnInit {
  public camera: SnCamera;
  public cameraSettings: any;
  public currentTab: string = 'General';
  public readonly form: UntypedFormGroup = new UntypedFormGroup({});
  public isLoading: boolean = false;

  public formFieldsGeneral: SnFormFieldConfig[] = [
    {
      templateOptions: {
        label: 'Camera Settings',
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-form-field-row',
      fieldGroup: [
        {
          key: 'cameraName',
          type: 'input',
          className: 'fusus-camera-form-field-col-12',
          templateOptions: {
            label: 'Camera Name',
          },
        },
      ],
    },
    {
      templateOptions: {
        label: 'Location',
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-form-field-row',
      fieldGroup: [
        {
          key: 'location',
          type: 'http-select',
          className: 'fusus-camera-form-field-col-6',
          templateOptions: {
            label: 'Location',
            floatLabel: 'always',
            placeholder: 'None',
            infinityScroll: true,
            valueProp: 'id',
            labelProp: 'name',
            search: true,
            fetchDataFn: (params: ISnListQueryParameters) =>
              this._locationsAPI.getLocationList({ ...params }),
          },
        },
        this._floorFieldsGenerator.getFloorSelectField('location', {
          className: 'fusus-camera-form-field-col-6',
          hooks: {
            onInit: (field: ISnHttpSelectFormFieldConfig) => {
              field.parent.formControl
                .get('location')
                .valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroy$))
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                  field.reset();
                });
            },
          },
        }),
      ],
    },
    {
      templateOptions: {
        label: 'Heading',
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-form-field-row-map',
      fieldGroup: [
        {
          key: 'heading',
          type: 'radial',

          templateOptions: {
            change: (field) => {
              this.formModel.heading = field.formControl.value;
            },
          },
        },
        this._cameraFieldsGeneratorService.getGeometryField({
          defaultValue: this.data.camera.geometry
            ? this.data.camera.geometry
            : this._currentOrganization.settings.geometry,
          templateOptions: {
            mapOptions: {
              center:
                this.data.camera.geometry?.coordinates ||
                this._currentOrganization.settings.geometry.coordinates,
            },
          },
        }),
      ],
    },
  ];

  public formFieldsStream: SnFormFieldConfig[] = [
    {
      templateOptions: {
        label: 'Video Settings',
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-form-field-row-grid',
      fieldGroup: [
        {
          key: 'brightnessPercentage',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Brightness Percentage',
            placeholder: 'Choose Brightness Level',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'contrastPercentage',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Contrast Percentage',
            placeholder: 'Choose Contrast Level',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'saturationPercentage',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Saturation Percentage',
            placeholder: 'Choose Saturation Level',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'sharpnessPercentage',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Sharpness Percentage',
            placeholder: 'Choose Sharpness Level',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
      ],
    },
    {
      templateOptions: {
        label: 'Audio Settings',
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'fusus-camera-form-field-row-grid',
      fieldGroup: [
        {
          key: 'boostMic',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Boost Microphone',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'gain',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Gain',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'volumeInternal',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'Internal Volume',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'volumeExternal',
          type: 'slider',
          defaultValue: 50,
          templateOptions: {
            label: 'External Volume',
            min: 0,
            max: 100,
            thumbLabel: true,
          },
        },
        {
          key: 'mute',
          type: 'checkbox',
          className: 'fusus-camera-form-field-col-2',
          templateOptions: {
            label: 'Mute',
            placeholder: 'Mute Camera',
          },
        },
      ],
    },
  ];

  public formModel: any = {};
  public detections: any[] = [];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      camera: SnCamera;
      cameraSettings: any;
    },
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _cameraFieldsGeneratorService: SnCameraFieldsGeneratorService,
    private readonly _dialogRef: MatDialogRef<FususCameraFormDialogComponent>,
    private readonly _camerasService: SnCamerasAPI,
    private readonly _locationsAPI: SnLocationsAPI,
    private readonly _floorFieldsGenerator: SnFloorFieldsGeneratorService,
    private readonly _matDialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
  ) {
    this.isLoading = true;
    this.camera = this.data.camera;
    this.cameraSettings = this.data.cameraSettings;
    this.detections = this.data.camera.aiDetectionTypes;
    this._patchForm();
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitStream(): void {
    const videoSources = {
      c0: {
        image: {
          contrastpercent: this.formModel.contrastPercentage.toString(),
          brightnesspercent: this.formModel.brightnessPercentage.toString(),
          saturationpercent: this.formModel.saturationPercentage.toString(),
          sharpnesspercent: this.formModel.sharpnessPercentage.toString(),
        },
      },
    };

    const audioSources = {
      c0: {
        boostMic: this.formModel.boostMic.toString(),
        gain: this.formModel.gain.toString(),
        volume_external: this.formModel.volumeExternal.toString(),
        volume_internal: this.formModel.volumeInternal.toString(),
      },
    };

    const cameraSettingsUpdate = {
      media: {
        video_sources: videoSources,
        audio_sources: audioSources,
      },
    };

    this._camerasService
      .updateCameraSettings(this.data.camera.id, cameraSettingsUpdate)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public submitGeneral(): void {
    const cameraUpdate = {
      cameraName: this.formModel.cameraName,
      geometry: this.formModel.geometry,
      location: this.formModel.location,
      floor: this.formModel.floor,
      heading: this.formModel.heading,
    };
    this._camerasService
      .updateCamera(this.data.camera.id, cameraUpdate)
      .pipe(takeUntil(this._destroy$))
      .subscribe((camera) => this._dialogRef.close(camera));
  }

  public createNewDetectionRule(): void {
    const ref = this._matDialog.open(FususCameraDetectionsDialogComponent, {
      data: {
        camera: this.camera,
      },
      width: '80vw',
      minWidth: '80vw',
      maxWidth: '80vw',
      height: '90vh',
    });
    ref
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          this.camera = res;
          this.detections = res.aiDetectionTypes;
          this._cd.detectChanges();
        }
        return;
      });
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.currentTab = event.tab.textLabel;
    this._cd.detectChanges();
  }

  public editAiDetection(aiDetection: any): void {
    this._matDialog.open(FususCameraDetectionsDialogComponent, {
      data: {
        camera: this.camera,
        detections: aiDetection,
      },
    });
  }

  public removeAiDetection(index: number): void {
    this.detections.splice(index, 1);
    this._camerasService.removeAiDetection(this.camera.id, this.detections);
  }

  private _patchForm(): void {
    this.formModel.cameraName = this.camera.name;
    this.formModel.location = this.camera?.location ? this.camera?.location?.id : null;
    this.formModel.floor = this.camera?.floor?.id;
    this.formModel.heading = this.camera.heading;
    this.formModel.contrastPercentage =
      this.cameraSettings.media.video_sources.c0.image.contrastpercent;
    this.formModel.brightnessPercentage =
      this.cameraSettings.media.video_sources.c0.image.brightnesspercent;
    this.formModel.saturationPercentage =
      this.cameraSettings.media.video_sources.c0.image.saturationpercent;
    this.formModel.sharpnessPercentage =
      this.cameraSettings.media.video_sources.c0.image.sharpnesspercent;
    this.formModel.boostMic = this.cameraSettings.media.audio_sources.c0.boostMic;
    this.formModel.gain = this.cameraSettings.media.audio_sources.c0.gain;
    this.formModel.volumeExternal = this.cameraSettings.media.audio_sources.c0.volume_external;
    this.formModel.volumeInternal = this.cameraSettings.media.audio_sources.c0.volume_internal;
    this.formModel.mute = this.cameraSettings.media.audio_sources.c0.mute;
  }
}
